import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLoaderData } from "@tanstack/react-router";
import type { BlogListPost } from "@/lib/seo/blogList";
import { useSearchParams, useLocation } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { Loader2, Filter, X } from "lucide-react";
import KnowledgeHeroAnimation from "@/components/KnowledgeHeroAnimation";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogSEO from "@/components/blog/BlogSEO";
import BlogCard from "@/components/blog/BlogCard";
import BlogCardSkeleton from "@/components/blog/BlogCardSkeleton";
import BlogFilterSidebar, { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";
import BlogSearch from "@/components/blog/BlogSearch";
import FeaturedPost from "@/components/blog/FeaturedPost";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { expandFilterAliases } from "@/lib/filterAliases";
import { EXCLUDED_TAGS } from "@/lib/resourceTypes";

const hasExcludedTag = (tags?: string[] | null) =>
  (tags ?? []).some((t) => EXCLUDED_TAGS.includes(t.toLowerCase()));


const PAGE_SIZE = 12;
const INITIAL_VISIBLE = 13;

const BLOG_UI = {
  en: { filters: "Filters", clearAll: "Clear all", articles: "articles", noResults: (q: string) => `No results for "${q}".`, noFilter: (f: string) => `No posts found for "${f}".`, noPosts: "No blog posts yet.", clearAllFilters: "Clear all filters", endReached: "You've reached the end", searchPlaceholder: "Search articles..." },
  es: { filters: "Filtros", clearAll: "Borrar todo", articles: "artículos", noResults: (q: string) => `Sin resultados para "${q}".`, noFilter: (f: string) => `No se encontraron publicaciones para "${f}".`, noPosts: "Aún no hay publicaciones.", clearAllFilters: "Borrar todos los filtros", endReached: "Has llegado al final", searchPlaceholder: "Buscar artículos..." },
  "zh-cn": { filters: "筛选", clearAll: "清除全部", articles: "篇文章", noResults: (q: string) => `未找到“${q}”的结果。`, noFilter: (f: string) => `未找到“${f}”的文章。`, noPosts: "暂无博客文章。", clearAllFilters: "清除所有筛选", endReached: "已到达末尾", searchPlaceholder: "搜索文章..." },
} as const;

const Blog = () => {
  const { pathname } = useLocation();
  const lang: "en" | "es" | "zh-cn" = pathname.startsWith("/zh-cn") ? "zh-cn" : pathname.startsWith("/es") ? "es" : "en";
  const t = BLOG_UI[lang];
  const blogPrefix = lang === "es" ? "/es/blog" : lang === "zh-cn" ? "/zh-cn/blog" : "/en/blog";
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter");
  const [selectedFilters, setSelectedFilters] = useState<string[]>(initialFilter ? [initialFilter] : []);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  // Server-rendered post list from the route loader: present in the initial
  // HTML so crawlers see every article link without running JavaScript.
  const loaderPosts = useLoaderData({ strict: false }) as BlogListPost[] | undefined;

  // Fetch featured post (most recent, with content for reading time), excluding hidden tags
  const { data: featuredPostRaw } = useQuery({
    queryKey: ["blog-featured", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, summary, featured_image, category, industry_category, service_category, published_at, author, content, tags")
        .eq("lang", lang)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []).find((p) => !hasExcludedTag(p.tags)) ?? null;
    },
  });

  // Fetch ALL posts once, then filter client-side (parity with Knowledge Center)
  const { data: fetchedPosts, isLoading: isQueryLoading } = useQuery({
    queryKey: ["blog-posts-all", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, summary, featured_image, category, industry_category, service_category, published_at, author, tags, content")
        .eq("lang", lang)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []).filter((p) => !hasExcludedTag(p.tags));
    },
  });
  const allFetchedPosts = fetchedPosts ?? loaderPosts;
  const isLoading = isQueryLoading && !loaderPosts;

  // Reset visible count whenever filters/search change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedFilters, searchQuery]);

  // Apply filters + search client-side, mirroring Resources logic
  const filteredPosts = useMemo(() => {
    let list = allFetchedPosts ?? [];

    if (selectedFilters.length > 0) {
      list = list.filter((r: any) => {
        const rCat = (r.category ?? "").toLowerCase();
        const rIndustry = (r.industry_category ?? "").toLowerCase();
        const rService = (r.service_category ?? "").toLowerCase();
        const rTags = (r.tags ?? []).map((t: string) => t.toLowerCase());
        return selectedFilters.every((filter) => {
          const aliases = expandFilterAliases([filter]).map((a) => a.toLowerCase());
          return aliases.some((a) => rCat === a || rIndustry === a || rService === a || rTags.includes(a));
        });
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r: any) =>
          (r.title ?? "").toLowerCase().includes(q) ||
          (r.summary ?? "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [allFetchedPosts, selectedFilters, searchQuery]);

  const { data: allFilters } = useQuery({
    queryKey: ["blog-filters", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("category, industry_category, service_category, tags")
        .eq("lang", lang)
        .not("published_at", "is", null);
      if (error) throw error;
      const cats = data.flatMap((d: any) => [d.category, d.industry_category, d.service_category]).filter(Boolean);
      const tags = data.flatMap((d: any) => d.tags || []);
      return [...new Set([...cats, ...tags])].sort();
    },
  });

  // Exclude the featured post from the grid when no filters/search active.
  // Fall back to the loader's first post so the featured card is in the SSR HTML.
  const featuredPost = featuredPostRaw ?? allFetchedPosts?.[0] ?? null;
  const showFeatured = selectedFilters.length === 0 && !searchQuery && featuredPost;
  const visibleFiltered = showFeatured
    ? filteredPosts.filter((p: any) => p.id !== featuredPost?.id)
    : filteredPosts;
  const posts = visibleFiltered.slice(0, visibleCount);
  const hasNextPage = visibleCount < visibleFiltered.length;
  const isFetchingNextPage = false;

  const fetchNextPage = useCallback(() => {
    setVisibleCount((c) => c + PAGE_SIZE);
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const handleSearch = useCallback((q: string) => setSearchQuery(q), []);

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />
      <BlogSEO type="listing" />

      {/* Hero */}
      <section className="relative pt-36 pb-20 sm:pb-28 bg-primary overflow-hidden">
        <KnowledgeHeroAnimation />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent/5 -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />

        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8 mt-4">
              <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
                <li>
                  <a href={lang === "es" ? "/es" : lang === "zh-cn" ? "/zh-cn" : "/"} className="hover:text-primary-foreground/70 transition-colors">
                    {lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home"}
                  </a>
                </li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li className="text-primary-foreground/60">
                  {lang === "es" ? "Blog de Life Sciences" : lang === "zh-cn" ? "生命科学博客" : "Life Sciences Blog"}
                </li>
              </ol>
            </nav>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight leading-[1.1]">
              {lang === "es" ? "Blog de Life Sciences" : lang === "zh-cn" ? "生命科学博客" : "Life Sciences Blog"}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-primary-foreground/60 max-w-xl leading-relaxed">
              {lang === "es"
                ? "Orientación experta en farmacia, biotecnología y dispositivos médicos — mantente al día con las tendencias y mejores prácticas de la industria."
                : lang === "zh-cn"
                ? "制药、生物技术和医疗器械领域的专家见解 — 紧跟行业趋势与最佳实践。"
                : "Expert guidance across pharma, biotech, and medical devices — stay ahead of industry trends and best practices."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-qbd-ice">
        <div className="container mx-auto px-6 lg:px-10">
          {/* Top bar */}
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden gap-2"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <Filter size={14} />
                {t.filters}
              </Button>
              {selectedFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setSelectedFilters(selectedFilters.filter((x) => x !== f))}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-primary/8 text-primary rounded-full hover:bg-primary/15 transition-colors"
                >
                  {FILTER_DISPLAY_NAMES[f] ?? f}
                  <X size={12} />
                </button>
              ))}
              {selectedFilters.length > 1 && (
                <button
                  onClick={() => setSelectedFilters([])}
                  className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t.clearAll}
                </button>
              )}
            </div>
            <span className="text-sm text-muted-foreground shrink-0">
              {visibleFiltered.length > 0 && `${visibleFiltered.length} ${t.articles}`}
            </span>
          </div>

          {/* Mobile filters */}
          {showMobileFilters && allFilters && (
            <div className="lg:hidden mb-6 bg-background border border-border rounded-xl p-4">
              <BlogFilterSidebar
                selectedFilters={selectedFilters}
                onFilterChange={(f) => {
                  setSelectedFilters(f);
                  setShowMobileFilters(false);
                }}
                availableFilters={allFilters}
              />
            </div>
          )}

          <div className="flex gap-10">
            {/* Desktop sidebar */}
            <div className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-36 space-y-6">
                <BlogSearch onSearch={handleSearch} placeholder={t.searchPlaceholder} />
                {allFilters && (
                  <BlogFilterSidebar
                    selectedFilters={selectedFilters}
                    onFilterChange={setSelectedFilters}
                    availableFilters={allFilters}
                  />
                )}
              </div>
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {/* Featured post */}
              {showFeatured && <FeaturedPost post={featuredPost} linkPrefix={blogPrefix} />}

              {isLoading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg mb-2">
                    {searchQuery
                      ? t.noResults(searchQuery)
                      : selectedFilters.length > 0
                      ? t.noFilter(selectedFilters.join(", "))
                      : t.noPosts}
                  </p>
                  {(searchQuery || selectedFilters.length > 0) && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedFilters([]);
                      }}
                      className="text-sm text-qbd-blue font-semibold hover:underline"
                    >
                      {t.clearAllFilters}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {posts.map((post, i) => (
                      <BlogCard key={post.id} post={post} index={i} linkPrefix={blogPrefix} />
                    ))}
                  </div>
                  <div ref={loadMoreRef} className="flex justify-center py-10">
                    {isFetchingNextPage && (
                      <Loader2 className="animate-spin text-muted-foreground" size={24} />
                    )}
                    {!hasNextPage && posts.length > PAGE_SIZE && (
                      <p className="text-sm text-muted-foreground">{t.endReached}</p>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
